import { useBlocker, useRouter } from "@tanstack/solid-router";
import { onCleanup } from "solid-js";

/**
 * Back-button-closes-overlay (premium PWA/mobile pattern).
 *
 * When an overlay (dialog/sheet/command-palette/non-routed drawer) is open
 * and the user presses the browser's "back", we want to CLOSE the overlay instead of
 * navigating in history.
 *
 * IMPORTANT — why we do NOT manipulate history by hand: TanStack Router patches
 * the History API (on the `window.history` instance) and, besides keeping an
 * index/key per entry, fires `router.load()` on every new entry. Pushing
 * a manual entry:
 *   - if it duplicates the router's index → `delta = 0` on popstate → loop → HANG;
 *   - if it increments it → the router treats it as real navigation, reloads the route and
 *     REMOUNTS the component, resetting the overlay's local signal → the overlay does NOT OPEN.
 * That's why we use the router's own mechanism: `useBlocker`. We don't touch
 * history on open (the overlay opens normally); we only intercept the "BACK" action:
 * we close the top overlay and cancel the navigation (the router does `go(1)`).
 *
 * LIFO: several nested overlays register their entry; "back" closes the most recent
 * one still open. It doesn't interfere with real navigation (PUSH/REPLACE/FORWARD pass through).
 *
 * ── CC-19: why the close is DEFERRED (one macrotask later) ──
 * When the blocker decides to block BACK, the router's library runs,
 * SYNCHRONOUSLY after our `shouldBlockFn`, `win.history.go(1)` to restore
 * the entry prior to the pop (canceling the "back"). If the overlay's `close()` in
 * turn NAVIGATES (URL-addressable overlays that toggle a search-param with
 * `replace:true`, e.g. `?preview=`/`?share=1`), that `replace` and the `go(1)`
 * compete over the History API: the `replace` lands on the WRONG entry
 * (the previous one, already popped) and the `go(1)` restores the URL with the param still set
 * → the router state and the address bar end up DESYNCED (the
 * dialog hides but the URL keeps the param, and on reload/re-navigate the
 * overlay BOUNCES back open). By deferring `close()` to a macrotask, it runs
 * AFTER `go(1)` settles, so the closing `replace` lands on the
 * correct entry and URL+state end up coherent. For local-signal overlays (without
 * navigation) the deferral is imperceptible and doesn't change the "back closes
 * the top" contract.
 */

interface OverlayEntry {
  isOpen: () => boolean;
  close: () => void;
}

const stack: OverlayEntry[] = [];

/**
 * Pure, testable logic: given a navigation action, decides whether it should CLOSE the
 * overlay (and return `true` to BLOCK the navigation) instead of navigating.
 * Only intercepts "BACK", and only if `entry` is the most recently opened overlay.
 */
export function resolveBackDismiss(
  action: string,
  entry: OverlayEntry,
  overlays: readonly OverlayEntry[] = stack,
): boolean {
  if (action !== "BACK") {
    return false;
  }
  const topmostOpen = overlays.findLast((candidate) => candidate.isOpen());
  if (topmostOpen === entry) {
    entry.close();
    return true;
  }
  return false;
}

/**
 * @param isOpen  reactive signal: `true` while the overlay is open.
 * @param close   closes the overlay (e.g. `() => onOpenChange(false)`).
 */
export function useBackDismiss(isOpen: () => boolean, close: () => void): void {
  // Without a RouterProvider (isolated component tests / SSR) the blocker doesn't apply;
  // back-dismiss is a progressive enhancement, so we no-op. In production
  // the overlay is always mounted inside the router.
  if (!useRouter({ warn: false })) {
    return;
  }

  // The close is DEFERRED to a macrotask (see the CC-19 header): it must run
  // after the `history.go(1)` the library runs when blocking BACK, so that
  // a `close()` that navigates (URL-addressable overlays) doesn't compete with
  // that restoration and leave URL/state desynced (a bouncing overlay).
  const deferredClose = () => {
    setTimeout(close, 0);
  };
  const entry: OverlayEntry = { isOpen, close: deferredClose };
  stack.push(entry);
  onCleanup(() => {
    const idx = stack.indexOf(entry);
    if (idx !== -1) {
      stack.splice(idx, 1);
    }
  });

  // `enableBeforeUnload: false` → we don't want the native "leave site?" prompt.
  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: ({ action }) => resolveBackDismiss(action, entry),
  });
}
